// src/features/ClientAnalysis/ClientAnalysisPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Visibility, 
  Analytics,
  People,
  TrendingUp,
  Search,
  ShowChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useClientData, CLIENT_CONFIG } from './hooks/useClientData';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as RechartsBarChart, 
  Bar, 
  Legend 
} from 'recharts';

// --- Компонент графіка для клієнта ---
const ClientChart = ({ data, type = 'line', height = 200 }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('client_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    value: item.value,
    fullDate: item.date
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return new Date(payload[0].payload.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: theme.palette.primary.dark }}
          />
        </LineChart>
      ) : (
        <RechartsBarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return new Date(payload[0].payload.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Bar 
            dataKey="value" 
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      )}
    </ResponsiveContainer>
  );
};

// --- Порівняльний графік для кількох клієнтів ---
const ComparisonChart = ({ clientsData, selectedClients, height = 300 }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  // Групуємо дані по датах
  const allDates = new Set();
  const clientDataMap = {};
  
  selectedClients.forEach(clientKey => {
    const clientData = clientsData[clientKey]?.data || [];
    clientDataMap[clientKey] = {};
    clientData.forEach(item => {
      const dateKey = new Date(item.date).toISOString().split('T')[0];
      allDates.add(dateKey);
      clientDataMap[clientKey][dateKey] = item.value;
    });
  });

  const chartData = Array.from(allDates)
    .sort()
    .map(date => {
      const entry = { date: new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }) };
      selectedClients.forEach(clientKey => {
        const config = CLIENT_CONFIG[clientKey];
        entry[config.clientName] = clientDataMap[clientKey][date] || 0;
      });
      return entry;
    })
    .slice(-30); // Останні 30 днів

  if (chartData.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('client_analysis_no_comparison_data')}
        </Typography>
      </Box>
    );
  }

  const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={50}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]} />
        <Legend />
        {selectedClients.map((clientKey, index) => {
          const config = CLIENT_CONFIG[clientKey];
          return (
            <Line 
              key={clientKey}
              type="monotone" 
              dataKey={config.clientName} 
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

// Картка клієнта з графіком
const ClientCard = ({ clientKey, data, statistics, config, onViewDetails }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const [chartType, setChartType] = useState('line');

  // Останні 10 записів для графіка
  const chartData = data.slice(-10);

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px 0 rgba(0,0,0,0.12)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          }
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Заголовок клієнта */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <People sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {config.clientName}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={config.unit} 
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={`${t('client_analysis_code')}: ${config.clientCode}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Графік */}
          {chartData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('client_analysis_trend')}
                </Typography>
                <Select
                  size="small"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="line">{t('chart_type_line')}</MenuItem>
                  <MenuItem value="bar">{t('chart_type_bar')}</MenuItem>
                </Select>
              </Box>
              <ClientChart data={chartData} type={chartType} height={120} />
            </Box>
          )}

          {/* Статистика */}
          {data.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('client_stats_total')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {statistics.total.toLocaleString('es-ES')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('client_stats_avg')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {statistics.average.toFixed(1)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Кнопка деталей */}
          <Box sx={{ mt: 'auto' }}>
            <Button 
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => onViewDetails(clientKey)}
              sx={{
                borderRadius: '8px',
                py: 1,
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {t('client_analysis_view_details')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default function ClientAnalysisPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedClients, setSelectedClients] = useState([]);

  const { allClientsData, loading, error } = useClientData();

  // Фільтруємо клієнтів за пошуком
  const filteredClients = useMemo(() => {
    const clients = Object.keys(CLIENT_CONFIG);
    
    if (!searchTerm) return clients;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return clients.filter(clientKey => {
      const config = CLIENT_CONFIG[clientKey];
      return (
        config.clientName.toLowerCase().includes(lowerSearchTerm) ||
        config.clientCode.toString().includes(searchTerm)
      );
    });
  }, [searchTerm]);

  const handleViewDetails = (clientKey) => {
    navigate(`/client-analysis/${clientKey}`);
  };

  // Клієнти з даними для порівняння
  const clientsWithData = useMemo(() => {
    return Object.keys(allClientsData).filter(key => allClientsData[key]?.data?.length > 0);
  }, [allClientsData]);

  // Статистика по всіх клієнтах
  const totalStats = useMemo(() => {
    return {
      totalClients: Object.keys(CLIENT_CONFIG).length,
      clientsWithData: clientsWithData.length
    };
  }, [clientsWithData]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок сторінки */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <Analytics sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              {t('client_analysis_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('client_analysis_subtitle')}
            </Typography>
          </Box>
        </Stack>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ background: alpha(theme.palette.primary.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {totalStats.totalClients}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('client_analysis_total_clients')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                  {totalStats.clientsWithData}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('client_analysis_with_data')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Пошук */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label={t('client_analysis_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                placeholder={t('client_analysis_search_placeholder')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('client_analysis_found')}: <strong>{filteredClients.length}</strong> {t('client_analysis_clients')}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Вкладки */}
      <Card sx={{ mb: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="fullWidth"
        >
          <Tab label={t('client_analysis_overview')} />
          <Tab 
            label={t('client_analysis_comparison')} 
            disabled={clientsWithData.length < 2}
          />
        </Tabs>
      </Card>

      {/* Контент вкладок */}
      {tabIndex === 0 ? (
        // Вкладка огляду
        <Grid container spacing={3}>
          {filteredClients.map(clientKey => (
            <ClientCard
              key={clientKey}
              clientKey={clientKey}
              data={allClientsData[clientKey]?.data || []}
              statistics={allClientsData[clientKey]?.statistics || {
                total: 0, average: 0, max: 0, min: 0, daysWithData: 0, recordsCount: 0
              }}
              config={CLIENT_CONFIG[clientKey]}
              onViewDetails={handleViewDetails}
            />
          ))}
        </Grid>
      ) : (
        // Вкладка порівняння
        <Box>
          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('client_analysis_select_clients')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>{t('client_analysis_clients')}</InputLabel>
              <Select
                multiple
                value={selectedClients}
                onChange={(e) => setSelectedClients(e.target.value)}
                renderValue={(selected) => selected.map(key => CLIENT_CONFIG[key].clientName).join(', ')}
              >
                {clientsWithData.map(clientKey => (
                  <MenuItem key={clientKey} value={clientKey}>
                    {CLIENT_CONFIG[clientKey].clientName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>

          {selectedClients.length > 0 && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                {t('client_analysis_comparison_chart')}
              </Typography>
              <ComparisonChart 
                clientsData={allClientsData} 
                selectedClients={selectedClients} 
                height={400}
              />
            </Card>
          )}
        </Box>
      )}

      {/* Інформаційний блок */}
      {totalStats.clientsWithData > 0 && (
        <Card 
          sx={{ 
            mt: 4, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {t('client_analysis_click_for_details')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


